import { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { contrastText } from '@/shared/lib/color';
import { Icons } from '@/shared/ui/icons';

import type { Label } from '@/entities/board/types';
import styles from './LabelGrid.module.css';

interface Props {
  labels: readonly Label[];
  selectedIds: readonly string[];
  onToggle: (labelId: string) => void;
  /** Autofocus the search field when it appears (popovers). */
  autoFocus?: boolean;
  /** Called for a label's own action (e.g. "edit"); omitted -> none. */
  disabled?: boolean;
}

interface OptionProps {
  label: Label;
  selected: boolean;
  disabled: boolean;
  unnamed: string;
  onToggle: (labelId: string) => void;
}

const LabelOption = memo(function LabelOption({
  label,
  selected,
  disabled,
  unnamed,
  onToggle,
}: OptionProps) {
  const name = label.name || unnamed;
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={selected}
      disabled={disabled}
      className={`${styles.option} ${selected ? styles.selected : ''}`}
      style={{ background: label.color, color: contrastText(label.color) }}
      title={name}
      onClick={() => {
        onToggle(label.id);
      }}
    >
      <span className={styles.name}>{name}</span>
      {selected && (
        <span className={styles.check}>
          <Icons.check />
        </span>
      )}
    </button>
  );
});

/**
 * Searchable, multi-select label chooser. The labels are laid out in a responsive grid (as many
 * columns as the width allows, never a single tall list) so 100 labels stay scannable. A chosen
 * label has a thin white outline AND a check mark; unchosen ones carry no border. Shared by the board
 * filter and by the card's label picker (they differ only in what a toggle does).
 */
export function LabelGrid({ labels, selectedIds, onToggle, autoFocus, disabled = false }: Props) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const selected = useMemo(() => new Set(selectedIds), [selectedIds]);
  const shown = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase();
    if (!needle) return labels;
    return labels.filter((label) => label.name.toLocaleLowerCase().includes(needle));
  }, [labels, query]);
  const unnamed = t('labels.unnamed');

  return (
    <div className={styles.root}>
      <div className={styles.search}>
        <Icons.search />
        <input
          type="search"
          value={query}
          placeholder={t('labelSelect.search')}
          aria-label={t('labelSelect.search')}
          autoFocus={autoFocus}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
        />
      </div>
      <div className={styles.grid} role="group" aria-label={t('labelSelect.labels')}>
        {shown.map((label) => (
          <LabelOption
            key={label.id}
            label={label}
            selected={selected.has(label.id)}
            disabled={disabled}
            unnamed={unnamed}
            onToggle={onToggle}
          />
        ))}
      </div>
      {shown.length === 0 && (
        <p className={styles.empty} role="status">
          {labels.length === 0 ? t('labelSelect.noLabels') : t('labelSelect.noMatches')}
        </p>
      )}
    </div>
  );
}
